import React, {useCallback, useEffect, useMemo, useState} from 'react';
import CIcon from "@coreui/icons-react";
import {Sessao, erroPortal, gravarSessao, lerSessao, portalApi, processoDaUrl, sessaoDaUrl} from "./api";
import {ProcessoResumoLista, Resumo, SITUACAO_PROCESSO, data} from "./tipos";
import {AgendaTela, DocumentosTela, HistoricoTela, PendenciasTela, ResumoTela, TimelineTela} from "./telas";
import '../../scss/styles-portal.scss';

type Aba = 'resumo' | 'timeline' | 'pendencias' | 'documentos' | 'agenda' | 'historico';

const ABAS: Array<[Aba, string, string]> = [
    ['resumo', 'Resumo', 'cil-speedometer'],
    ['timeline', 'Timeline', 'cil-list-numbered'],
    ['pendencias', 'Pendências', 'cil-task'],
    ['documentos', 'Documentos', 'cil-file'],
    ['agenda', 'Agenda', 'cil-calendar'],
    ['historico', 'Histórico', 'cil-history'],
];

const ABRIR_DE_NOVO = 'Abra o portal de novo pela Minha Conta do painel da sua loja (aba Implantação).';

/** Casca da pagina: cabecalho do portal e o conteudo. */
const Pagina = ({cliente, usuario, equipe, children}: {cliente?: string, usuario?: string, equipe?: boolean, children: React.ReactNode}) => (
    <div className="pc">
        <header className="pc-topo">
            <div className="pc-marca"><span className="pc-logo">P</span> <span>Portal da Implantação</span></div>
            {cliente && <div className="pc-topo-cliente">{cliente}</div>}
            {usuario && <div className="pc-topo-usuario"><CIcon name="cil-user"/> {usuario}</div>}
        </header>
        {equipe && (
            <div className="pc-equipe" role="note">
                <CIcon name="cil-info"/> Visualização da equipe: é assim que o cliente vê o portal. As ações do cliente ficam desligadas.
            </div>
        )}
        <main className="pc-conteudo pc-pagina">{children}</main>
    </div>
);

const Portal = ({sessao}: {sessao: Sessao}) => {
    const [expirou, setExpirou] = useState<string | null>(null);
    const api = useMemo(() => {
        const cliente = portalApi(sessao);
        // Sessao vencida ou invalida: nada mais funciona, entao troca a tela inteira pelo aviso.
        cliente.interceptors.response.use(undefined, erro => {
            if (erro?.response?.status === 401) {
                setExpirou(erroPortal(erro, 'Sua sessão expirou.'));
            }
            return Promise.reject(erro);
        });
        return cliente;
    }, [sessao]);
    const [processos, setProcessos] = useState<ProcessoResumoLista[] | null>(null);
    const [processoId, setProcessoId] = useState<number | null>(null);
    const [resumo, setResumo] = useState<Resumo | null>(null);
    const [aba, setAba] = useState<Aba>('resumo');
    const [versao, setVersao] = useState(0);
    const [aviso, setAviso] = useState<{texto: string, tipo: 'ok' | 'erro'} | null>(null);

    const avisar = useCallback((texto: string, tipo: 'ok' | 'erro' = 'ok') => {
        setAviso({texto, tipo});
        window.setTimeout(() => setAviso(atual => (atual && atual.texto === texto ? null : atual)), 5000);
    }, []);

    useEffect(() => {
        api.get('/portal/processos')
            .then(r => {
                setProcessos(r.data.processos);
                const pedido = r.data.processos.find((p: ProcessoResumoLista) => p.id === processoDaUrl());
                const aberto = pedido ?? r.data.processos.find((p: ProcessoResumoLista) => p.situacao === 'em_andamento') ?? r.data.processos[0];
                setProcessoId(aberto ? aberto.id : null);
            })
            .catch(e => avisar(erroPortal(e, 'Não foi possível carregar seus processos.'), 'erro'));
    }, [api]);

    useEffect(() => {
        if (!processoId) return;
        api.get(`/portal/processo/${processoId}/resumo`)
            .then(r => setResumo(r.data))
            .catch(e => avisar(erroPortal(e, 'Não foi possível carregar o processo.'), 'erro'));
    }, [api, processoId, versao]);

    if (expirou) {
        return <Pagina><div className="pc-vazio"><strong>{expirou}</strong><br/>{ABRIR_DE_NOVO}</div></Pagina>;
    }

    const atualizar = () => setVersao(v => v + 1);
    const situacao = resumo ? (SITUACAO_PROCESSO[resumo.situacao] || {rotulo: resumo.situacao, classe: ''}) : null;
    const contexto = {api, processoId: processoId!, versao, atualizar, avisar, irPara: (destino: Aba) => setAba(destino), leitura: !!sessao.equipe};

    return (
        <Pagina cliente={resumo?.cliente} usuario={sessao.usuario.nome} equipe={sessao.equipe}>
            {processos !== null && processos.length === 0 && (
                <div className="pc-vazio">Nenhum processo de implantação para a sua empresa.</div>
            )}
            {resumo && situacao && (
                <>
                    <section className="pc-cabecalho">
                        <div className="pc-cabecalho-texto">
                            <span className="pc-sobre">{processos?.find(p => p.id === processoId)?.fluxo || 'Implantação do e-commerce'}</span>
                            <h1>{resumo.titulo}</h1>
                            <span className="pc-sub">
                                Consultor(a): <strong>{resumo.consultor}</strong> · início {data(resumo.inicio)} · previsão {data(resumo.previsao)}
                            </span>
                        </div>
                        <div className="pc-cabecalho-lado">
                            {processos && processos.length > 1 && (
                                <select value={processoId ?? ''} onChange={e => setProcessoId(Number(e.target.value))} className="pc-seletor">
                                    {processos.map(p => <option key={p.id} value={p.id}>{p.titulo}</option>)}
                                </select>
                            )}
                            <span className={`pc-situacao ${situacao.classe}`}>{situacao.rotulo}</span>
                            <div className="pc-progresso">
                                <div className="pc-barra"><span style={{width: `${resumo.percentual}%`}}/></div>
                                <strong>{Math.round(resumo.percentual)}%</strong>
                            </div>
                            <span className="pc-sub">{resumo.etapas.concluidas} de {resumo.etapas.total} etapas concluídas</span>
                        </div>
                    </section>

                    <nav className="pc-abas" role="tablist">
                        {ABAS.map(([chave, rotulo, icone]) => (
                            <button type="button" role="tab" aria-selected={aba === chave} key={chave} className={aba === chave ? 'ativa' : ''} onClick={() => setAba(chave)}>
                                <CIcon name={icone}/> {rotulo}
                                {chave === 'pendencias' && resumo.pendencias.cliente > 0 && <span className="pc-contador">{resumo.pendencias.cliente}</span>}
                            </button>
                        ))}
                    </nav>

                    {aba === 'resumo' && <ResumoTela resumo={resumo} {...contexto}/>}
                    {aba === 'timeline' && <TimelineTela {...contexto}/>}
                    {aba === 'pendencias' && <PendenciasTela {...contexto}/>}
                    {aba === 'documentos' && <DocumentosTela {...contexto}/>}
                    {aba === 'agenda' && <AgendaTela {...contexto}/>}
                    {aba === 'historico' && <HistoricoTela {...contexto}/>}
                </>
            )}
            {aviso && <div className={`pc-aviso ${aviso.tipo}`} role="status">{aviso.texto}</div>}
        </Pagina>
    );
};

/** Le (sem validar: quem valida e o servidor) quem esta logado, gravado na sessao assinada. */
const lerCarga = (codigo: string): {usuario: {nome: string, email: string}, equipe: boolean} => {
    try {
        const bytes = atob(codigo.split('.')[0].replace(/-/g, '+').replace(/_/g, '/'));
        // base64 de JSON em UTF-8 (nomes com acento)
        const carga = JSON.parse(decodeURIComponent(Array.from(bytes, c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')));
        return {usuario: {nome: carga?.a?.nome || '', email: carga?.a?.email || ''}, equipe: !!carga?.v};
    } catch (e) {
        return {usuario: {nome: '', email: ''}, equipe: false};
    }
};

/**
 * Portal do cliente (#480058), rota publica #/portal: aberto pela plataforma do
 * cliente (Minha Conta > Implantacao > "Abrir o portal"), sem login do admin, so
 * com a sessao curta que o servidor da plataforma pediu em POST /portal/sessao.
 * A sessao chega na URL, vai para o sessionStorage da aba e sai da barra de
 * endereco (nao fica no historico nem e copiada junto com o link).
 */
const PortalCliente = () => {
    const sessao = useMemo(() => {
        const daUrl = sessaoDaUrl();
        if (daUrl) {
            gravarSessao(daUrl);
            // Tira a sessao da barra; o processo pedido (link da equipe) fica.
            const processo = processoDaUrl();
            window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#/portal${processo ? `?processo=${processo}` : ''}`);
        }
        const codigo = daUrl || lerSessao();
        return codigo ? {sessao: codigo, ...lerCarga(codigo)} as Sessao : null;
    }, []);
    if (!sessao) {
        return <Pagina><div className="pc-vazio">{ABRIR_DE_NOVO}</div></Pagina>;
    }
    return <Portal sessao={sessao}/>;
};

export default PortalCliente;
